import Dexie, { type Table } from 'dexie';
import type { Engagement, Entity, TBAccount, JournalEntryLine, JournalEntryHeader, SignoffState } from '../types/index.ts';

// ============================================================================
// SUBGROUPING TYPE (Phase 3)
// ============================================================================

/**
 * Persistent SubGrouping entity.
 * Unique constraint enforced in application logic: (engagementId, name)
 */
export interface SubGrouping {
  id?: string;
  engagementId: string;
  name: string;
}

// ============================================================================
// ENGAGEMENT BUNDLE TYPE
// ============================================================================

/**
 * A "Deep Bundle" — the canonical export/import unit for a .TAXTRAIL file.
 *
 * All integer-cent values (bookBalance, rje, aje, etc.) survive a
 * JSON round-trip intact because JSON.stringify / JSON.parse preserve
 * JavaScript integers without any rounding.
 *
 * version is used for forward-compat detection. Currently "1".
 */
export interface EngagementBundle {
  version: 1;
  engagement: Engagement;
  accounts: TBAccount[];
  subGroupings: SubGrouping[];
}

// ============================================================================
// DATABASE DEFINITION
// ============================================================================

class TaxTrailDatabase extends Dexie {
  // Tables
  engagements!: Table<Engagement, string>;
  accounts!: Table<TBAccount, string>;
  journalEntryLines!: Table<JournalEntryLine, string>;
  journalEntryHeaders!: Table<JournalEntryHeader, string>;
  subGroupings!: Table<SubGrouping, string>;
  entities!: Table<Entity, string>;

  constructor() {
    super('TaxTrailDB');

    // Schema version 1
    this.version(1).stores({
      engagements: 'id, clientName, taxYearEnd, taxForm, createdAt',
      accounts: 'id, engagementId, accountNumber, entity, grouping, createdAt',
      journalEntries: 'id, engagementId, accountId, type, createdAt',
    });

    // Schema version 2 — Phase 3: SubGroupings table
    this.version(2).stores({
      engagements: 'id, clientName, taxYearEnd, taxForm, createdAt',
      accounts: 'id, engagementId, accountNumber, entity, grouping, createdAt',
      journalEntries: 'id, engagementId, accountId, type, createdAt',
      subGroupings: 'id, engagementId, name',
    });

    // Schema version 3 — Phase 4: Journal Entry Headers + rename journalEntries → journalEntryLines
    this.version(3)
      .stores({
        engagements:         'id, clientName, taxYearEnd, taxForm, createdAt',
        accounts:            'id, engagementId, accountNumber, entity, grouping, createdAt',
        journalEntries:      null,  // drop old table
        journalEntryLines:   'id, engagementId, headerId, accountId, type, createdAt',
        journalEntryHeaders: 'id, engagementId, jeNumber, isRJE, signoffState',
        subGroupings:        'id, engagementId, name',
      })
      .upgrade(async (tx) => {
        // Preserve any existing journalEntries rows by copying them to journalEntryLines
        // with a blank headerId so the FK index is satisfied.
        const oldRows = await tx.table('journalEntries').toArray();
        if (oldRows.length > 0) {
          const migrated = oldRows.map((row: Record<string, unknown>) => ({
            ...row,
            headerId: row.headerId ?? '',
          }));
          await tx.table('journalEntryLines').bulkAdd(migrated);
        }
      });

    // Schema version 4 — Sprint 4.6: Add notes field to journalEntryHeaders
    this.version(4)
      .stores({
        engagements:         'id, clientName, taxYearEnd, taxForm, createdAt',
        accounts:            'id, engagementId, accountNumber, entity, grouping, createdAt',
        journalEntryLines:   'id, engagementId, headerId, accountId, type, createdAt',
        journalEntryHeaders: 'id, engagementId, jeNumber, isRJE, signoffState',
        subGroupings:        'id, engagementId, name',
      })
      .upgrade(async (tx) => {
        await tx.table('journalEntryHeaders').toCollection().modify((h: Record<string, unknown>) => {
          if (h.notes === undefined) h.notes = '';
        });
      });

    // Schema version 5 — Phase 4.16: Add cautionFlag field to journalEntryHeaders
    this.version(5)
      .stores({
        engagements:         'id, clientName, taxYearEnd, taxForm, createdAt',
        accounts:            'id, engagementId, accountNumber, entity, grouping, createdAt',
        journalEntryLines:   'id, engagementId, headerId, accountId, type, createdAt',
        journalEntryHeaders: 'id, engagementId, jeNumber, isRJE, signoffState',
        subGroupings:        'id, engagementId, name',
      })
      .upgrade(async (tx) => {
        await tx.table('journalEntryHeaders').toCollection().modify((h: Record<string, unknown>) => {
          if (h.cautionFlag === undefined) h.cautionFlag = false;
        });
      });

    // Schema version 6 — Phase 4.18: Add audit timestamp fields to journalEntryHeaders
    // - updatedAt: unix ms timestamp updated on every JE save
    // - lastPrincipalReviewAt: unix ms timestamp set when Principal tier is signed
    // cautionFlag is now deprecated; caution is computed from updatedAt > lastPrincipalReviewAt
    this.version(6)
      .stores({
        engagements:         'id, clientName, taxYearEnd, taxForm, createdAt',
        accounts:            'id, engagementId, accountNumber, entity, grouping, createdAt',
        journalEntryLines:   'id, engagementId, headerId, accountId, type, createdAt',
        journalEntryHeaders: 'id, engagementId, jeNumber, isRJE, signoffState',
        subGroupings:        'id, engagementId, name',
      })
      .upgrade(async (tx) => {
        const now = Date.now();
        await tx.table('journalEntryHeaders').toCollection().modify((h: Record<string, unknown>) => {
          if (h.updatedAt === undefined) h.updatedAt = now;
          if (h.lastPrincipalReviewAt === undefined) h.lastPrincipalReviewAt = null;
        });
      });

    // Schema version 7 — Sprint 5.1: Add differenceType field to journalEntryHeaders
    // - differenceType: 'Permanent' | 'Temporary' — M-Adj tax difference classification
    this.version(7)
      .stores({
        engagements:         'id, clientName, taxYearEnd, taxForm, createdAt',
        accounts:            'id, engagementId, accountNumber, entity, grouping, createdAt',
        journalEntryLines:   'id, engagementId, headerId, accountId, type, createdAt',
        journalEntryHeaders: 'id, engagementId, jeNumber, isRJE, signoffState',
        subGroupings:        'id, engagementId, name',
      })
      .upgrade(async (tx) => {
        await tx.table('journalEntryHeaders').toCollection().modify((h: Record<string, unknown>) => {
          if (h.differenceType === undefined) h.differenceType = 'Permanent';
        });
      });

    // Schema version 8 — Phase 5: Add calcType and calcInputs fields to journalEntryHeaders
    // - calcType: workpaper calculation mode (defaults to 'Manual')
    // - calcInputs: calculation parameters for non-manual modes (defaults to {})
    this.version(8)
      .stores({
        engagements:         'id, clientName, taxYearEnd, taxForm, createdAt',
        accounts:            'id, engagementId, accountNumber, entity, grouping, createdAt',
        journalEntryLines:   'id, engagementId, headerId, accountId, type, createdAt',
        journalEntryHeaders: 'id, engagementId, jeNumber, isRJE, signoffState',
        subGroupings:        'id, engagementId, name',
      })
      .upgrade(async (tx) => {
        await tx.table('journalEntryHeaders').toCollection().modify((h: Record<string, unknown>) => {
          if (h.calcType === undefined) h.calcType = 'Manual';
          if (h.calcInputs === undefined) h.calcInputs = {};
        });
      });

    // Schema version 9 — Entity Management: Add entities lookup table
    // Seeds two default entities (clientName + Eliminations) for all existing engagements
    this.version(9)
      .stores({
        engagements:         'id, clientName, taxYearEnd, taxForm, createdAt',
        accounts:            'id, engagementId, accountNumber, entity, grouping, createdAt',
        journalEntryLines:   'id, engagementId, headerId, accountId, type, createdAt',
        journalEntryHeaders: 'id, engagementId, jeNumber, isRJE, signoffState',
        subGroupings:        'id, engagementId, name',
        entities:            'id, engagementId, name',
      })
      .upgrade(async (tx) => {
        const engagements = await tx.table('engagements').toArray() as Engagement[];
        for (const eng of engagements) {
          if (!eng.id) continue;
          await tx.table('entities').bulkAdd([
            { id: crypto.randomUUID(), engagementId: eng.id, name: eng.clientName },
            { id: crypto.randomUUID(), engagementId: eng.id, name: 'Eliminations' },
          ]);
        }
      });

    // Schema version 10 — Multi-Entity Posting Isolation
    // Wipes v9-seeded entities: new design starts with 0 entities per engagement (user-initiated only).
    // entityId on journalEntryHeaders is a non-indexed field (no schema change needed).
    this.version(10)
      .stores({
        engagements:         'id, clientName, taxYearEnd, taxForm, createdAt',
        accounts:            'id, engagementId, accountNumber, entity, grouping, createdAt',
        journalEntryLines:   'id, engagementId, headerId, accountId, type, createdAt',
        journalEntryHeaders: 'id, engagementId, jeNumber, isRJE, signoffState',
        subGroupings:        'id, engagementId, name',
        entities:            'id, engagementId, name',
      })
      .upgrade(async (tx) => {
        await tx.table('entities').clear();
      });
  }
}

// ============================================================================
// DATABASE INSTANCE
// ============================================================================

export const db = new TaxTrailDatabase();

// ============================================================================
// VALIDATION RULES (from System_Foundation.md)
// ============================================================================

/**
 * TB-001: Duplicate Prevention
 * No duplicate Account Numbers within scope (same engagement)
 */
export async function validateNoDuplicateAccountNumber(
  engagementId: string,
  accountNumber: string,
  excludeAccountId?: string
): Promise<boolean> {
  const existing = await db.accounts
    .where('engagementId')
    .equals(engagementId)
    .and(
      (acc) =>
        acc.accountNumber === accountNumber && acc.id !== excludeAccountId
    )
    .first();

  return !existing;
}

/**
 * JE-001: Duplicate JE Number Prevention
 * No duplicate jeNumber within the same engagement
 */
export async function validateNoDuplicateJENumber(
  engagementId: string,
  jeNumber: string,
  excludeHeaderId?: string
): Promise<boolean> {
  const existing = await db.journalEntryHeaders
    .where('engagementId')
    .equals(engagementId)
    .and(
      (h) => h.jeNumber === jeNumber && h.id !== excludeHeaderId
    )
    .first();
  return !existing; // true = valid (no duplicate)
}

/**
 * TB-002: Prior Year Protection
 * Deletion prohibited if any PY value is non-zero
 */
export async function canDeleteAccount(accountId: string): Promise<boolean> {
  const account = await db.accounts.get(accountId);
  if (!account) return false;

  // Cannot delete if prior year balance is non-zero
  return account.pyBalance === 0;
}

// ============================================================================
// SUBGROUPING CRUD (Phase 3)
// ============================================================================

/**
 * Get all SubGroupings for an engagement
 */
export async function getSubGroupings(
  engagementId: string
): Promise<SubGrouping[]> {
  return await db.subGroupings
    .where('engagementId')
    .equals(engagementId)
    .toArray();
}

/**
 * Check for duplicate SubGrouping name within the same engagement
 */
export async function validateNoDuplicateSubGrouping(
  engagementId: string,
  name: string,
  excludeId?: string
): Promise<boolean> {
  const existing = await db.subGroupings
    .where('engagementId')
    .equals(engagementId)
    .filter(
      (sg) =>
        sg.name.toLowerCase() === name.toLowerCase() && sg.id !== excludeId
    )
    .first();
  return !existing;
}

/**
 * Create a new SubGrouping
 * Returns the created SubGrouping's ID
 */
export async function createSubGrouping(
  engagementId: string,
  name: string
): Promise<string> {
  const isUnique = await validateNoDuplicateSubGrouping(engagementId, name);
  if (!isUnique) {
    throw new Error(`SubGrouping "${name}" already exists.`);
  }

  const id = crypto.randomUUID();
  await db.subGroupings.add({ id, engagementId, name });
  return id;
}

/**
 * Delete a SubGrouping with integrity cleanup.
 * When a SubGrouping is deleted, all accounts tagged with that name
 * have their subGrouping field reverted to "" (blank).
 */
export async function deleteSubGrouping(subGroupingId: string): Promise<number> {
  const subGrouping = await db.subGroupings.get(subGroupingId);
  if (!subGrouping) throw new Error('SubGrouping not found');

  let affectedCount = 0;

  await db.transaction('rw', [db.subGroupings, db.accounts], async () => {
    // Find all accounts tagged with this SubGrouping name in the same engagement
    const affectedAccounts = await db.accounts
      .where('engagementId')
      .equals(subGrouping.engagementId)
      .and((acc) => acc.subGrouping === subGrouping.name)
      .toArray();

    affectedCount = affectedAccounts.length;

    // Revert their subGrouping to ""
    for (const acc of affectedAccounts) {
      await db.accounts.update(acc.id!, { subGrouping: '', updatedAt: Date.now() });
    }

    // Delete the SubGrouping record
    await db.subGroupings.delete(subGroupingId);
  });

  return affectedCount;
}

/**
 * Rename a SubGrouping and cascade the new name to all tagged accounts.
 * Runs in a single Dexie transaction to guarantee consistency.
 */
export async function renameSubGrouping(
  subGroupingId: string,
  newName: string
): Promise<void> {
  const subGrouping = await db.subGroupings.get(subGroupingId);
  if (!subGrouping) throw new Error('SubGrouping not found');

  const isUnique = await validateNoDuplicateSubGrouping(
    subGrouping.engagementId,
    newName,
    subGroupingId
  );
  if (!isUnique) {
    throw new Error(`"${newName}" already exists in this engagement.`);
  }

  const oldName = subGrouping.name;

  await db.transaction('rw', [db.subGroupings, db.accounts], async () => {
    await db.subGroupings.update(subGroupingId, { name: newName });

    const affectedAccounts = await db.accounts
      .where('engagementId')
      .equals(subGrouping.engagementId)
      .and((acc) => acc.subGrouping === oldName)
      .toArray();

    for (const acc of affectedAccounts) {
      await db.accounts.update(acc.id!, { subGrouping: newName, updatedAt: Date.now() });
    }
  });
}

/**
 * Count accounts tagged with a specific SubGrouping name in an engagement
 */
export async function countAccountsWithSubGrouping(
  engagementId: string,
  subGroupingName: string
): Promise<number> {
  return await db.accounts
    .where('engagementId')
    .equals(engagementId)
    .and((acc) => acc.subGrouping === subGroupingName)
    .count();
}

// ============================================================================
// JOURNAL ENTRY HEADER CRUD (Phase 4)
// ============================================================================

export async function getJournalEntryHeaders(
  engagementId: string
): Promise<JournalEntryHeader[]> {
  return await db.journalEntryHeaders
    .where('engagementId')
    .equals(engagementId)
    .toArray();
}

export async function getJournalEntryHeader(
  id: string
): Promise<JournalEntryHeader | undefined> {
  return await db.journalEntryHeaders.get(id);
}

export async function createJournalEntryHeader(
  header: Omit<JournalEntryHeader, 'id'>
): Promise<string> {
  const id = crypto.randomUUID();
  await db.journalEntryHeaders.add({ ...header, id });
  return id;
}

export async function updateJournalEntryHeader(
  header: JournalEntryHeader
): Promise<void> {
  if (!header.id) throw new Error('JournalEntryHeader ID is required');
  await db.journalEntryHeaders.put(header);
}

export async function deleteJournalEntryHeader(id: string): Promise<void> {
  await db.transaction('rw', [db.journalEntryHeaders, db.journalEntryLines], async () => {
    await db.journalEntryLines.where('headerId').equals(id).delete();
    await db.journalEntryHeaders.delete(id);
  });
}

export async function updateSignoffState(
  headerId: string,
  signoffState: SignoffState
): Promise<void> {
  const patch: Partial<JournalEntryHeader> = { signoffState };
  if (signoffState === 'Principal') patch.lastPrincipalReviewAt = Date.now();
  await db.journalEntryHeaders.update(headerId, patch);
}

export async function acknowledgePrincipalReview(headerId: string): Promise<void> {
  await db.journalEntryHeaders.update(headerId, { lastPrincipalReviewAt: Date.now() });
}

// ============================================================================
// JOURNAL ENTRY LINES CRUD (Sprint 4.3)
// ============================================================================

export async function getAccounts(engagementId: string): Promise<TBAccount[]> {
  return await db.accounts.where('engagementId').equals(engagementId).toArray();
}

export async function getJournalEntryLines(headerId: string): Promise<JournalEntryLine[]> {
  return await db.journalEntryLines.where('headerId').equals(headerId).toArray();
}

export async function getAllJournalEntryLines(engagementId: string): Promise<JournalEntryLine[]> {
  return db.journalEntryLines.where('engagementId').equals(engagementId).toArray();
}

/**
 * Atomically replace all lines for a JE header and update the header's totalDebits,
 * jeNumber, jeName, and notes in a single transaction.
 *
 * Throws if the new jeNumber is already taken by a different header.
 */
export async function saveJournalEntryLines(
  header: JournalEntryHeader,
  lines: Omit<JournalEntryLine, 'id'>[],
  updatedJeNumber: string,
  updatedJeName: string,
  updatedNotes: string = ''
): Promise<void> {
  if (!header.id) throw new Error('JournalEntryHeader ID is required');

  // Validate uniqueness of updated JE number before entering transaction
  if (updatedJeNumber !== header.jeNumber) {
    const isUnique = await validateNoDuplicateJENumber(
      header.engagementId,
      updatedJeNumber,
      header.id
    );
    if (!isUnique) {
      throw new Error(`JE number "${updatedJeNumber}" is already in use.`);
    }
  }

  await db.transaction('rw', [db.journalEntryHeaders, db.journalEntryLines], async () => {
    await db.journalEntryLines.where('headerId').equals(header.id!).delete();

    if (lines.length > 0) {
      const linesWithIds = lines.map((l) => ({ ...l, id: crypto.randomUUID() }));
      await db.journalEntryLines.bulkAdd(linesWithIds);
    }

    const totalDebits = lines.reduce((sum, l) => sum + (l.amount > 0 ? l.amount : 0), 0);
    await db.journalEntryHeaders.update(header.id!, {
      totalDebits,
      jeNumber: updatedJeNumber,
      jeName: updatedJeName,
      notes: updatedNotes,
      updatedAt: Date.now(),
    });
  });
}

/**
 * Like saveJournalEntryLines but for M-Adjustment headers.
 * Also persists netAdjustmentCents on the header.
 *
 * Atomically replaces all lines for the header and updates header fields
 * (totalDebits, jeNumber, jeName, notes, netAdjustmentCents, updatedAt).
 */
export async function saveMAdjLines(
  header: JournalEntryHeader,
  lines: Omit<JournalEntryLine, 'id'>[],
  updatedJeNumber: string,
  updatedJeName: string,
  updatedNotes: string,
  netAdjustmentCents: number,
  differenceType: 'Permanent' | 'Temporary',
  calcType?: JournalEntryHeader['calcType'],
  calcInputs?: Record<string, number>
): Promise<void> {
  if (!header.id) throw new Error('JournalEntryHeader ID is required');

  if (updatedJeNumber !== header.jeNumber) {
    const isUnique = await validateNoDuplicateJENumber(
      header.engagementId,
      updatedJeNumber,
      header.id
    );
    if (!isUnique) {
      throw new Error(`M-Adj number "${updatedJeNumber}" is already in use.`);
    }
  }

  await db.transaction('rw', [db.journalEntryHeaders, db.journalEntryLines], async () => {
    await db.journalEntryLines.where('headerId').equals(header.id!).delete();

    if (lines.length > 0) {
      const linesWithIds = lines.map((l) => ({ ...l, id: crypto.randomUUID() }));
      await db.journalEntryLines.bulkAdd(linesWithIds);
    }

    const totalDebits = lines.reduce((sum, l) => sum + (l.amount > 0 ? l.amount : 0), 0);
    await db.journalEntryHeaders.update(header.id!, {
      totalDebits,
      jeNumber: updatedJeNumber,
      jeName: updatedJeName,
      notes: updatedNotes,
      netAdjustmentCents,
      differenceType,
      updatedAt: Date.now(),
      ...(calcType !== undefined && { calcType }),
      ...(calcInputs !== undefined && { calcInputs }),
    });
  });
}

/**
 * Update the M-Adj Ref link on a trial balance account.
 * Called when the user selects an M-Adj from the inline dropdown in TrialBalanceTable.
 */
export async function updateAccountMAdjRef(accountId: string, mAdjRef: string): Promise<void> {
  await db.accounts.update(accountId, { mAdjRef, updatedAt: Date.now() });
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all engagements sorted by client name
 */
export async function getAllEngagements(): Promise<Engagement[]> {
  return await db.engagements.orderBy('clientName').toArray();
}

/**
 * Get engagement by ID
 */
export async function getEngagement(id: string): Promise<Engagement | undefined> {
  return await db.engagements.get(id);
}

/**
 * Create new engagement
 */
export async function createEngagement(engagement: Engagement): Promise<string> {
  const id = engagement.id || crypto.randomUUID();
  await db.engagements.add({ ...engagement, id });
  await db.entities.add({ id: crypto.randomUUID(), engagementId: id, name: engagement.clientName });
  return id;
}

/**
 * Update existing engagement
 */
export async function updateEngagement(engagement: Engagement): Promise<void> {
  if (!engagement.id) throw new Error('Engagement ID is required');
  await db.engagements.put(engagement);
}

/**
 * Delete engagement and all related data
 */
export async function deleteEngagement(id: string): Promise<void> {
  await db.transaction(
    'rw',
    [db.engagements, db.accounts, db.journalEntryLines, db.journalEntryHeaders, db.subGroupings, db.entities],
    async () => {
      await db.engagements.delete(id);
      await db.accounts.where('engagementId').equals(id).delete();
      await db.journalEntryLines.where('engagementId').equals(id).delete();
      await db.journalEntryHeaders.where('engagementId').equals(id).delete();
      await db.subGroupings.where('engagementId').equals(id).delete();
      await db.entities.where('engagementId').equals(id).delete();
    }
  );
}

/**
 * Check if engagement with UUID already exists
 */
export async function engagementExists(id: string): Promise<boolean> {
  const existing = await db.engagements.get(id);
  return !!existing;
}

// ============================================================================
// ENTITY CRUD
// ============================================================================

export async function getEntities(engagementId: string): Promise<Entity[]> {
  return db.entities.where('engagementId').equals(engagementId).sortBy('name');
}

export async function createEntity(entity: Entity): Promise<void> {
  await db.entities.add(entity);
}

export async function renameEntity(id: string, name: string): Promise<void> {
  await db.entities.update(id, { name });
}

export async function deleteEntity(id: string): Promise<void> {
  await db.entities.delete(id);
}

// ============================================================================
// DEEP BUNDLE EXPORT HELPER
// ============================================================================

/**
 * getEngagementBundle
 *
 * Fetches an engagement and all of its related data (accounts + subGroupings)
 * in a single call, returning a self-contained EngagementBundle object.
 *
 * This bundle is what gets serialised into a .TAXTRAIL file, guaranteeing
 * that every related record travels with the engagement.
 *
 * Integer-cent values are preserved because JSON handles integers losslessly.
 *
 * @throws if the engagement is not found
 */
export async function getEngagementBundle(
  engagementId: string
): Promise<EngagementBundle> {
  const engagement = await db.engagements.get(engagementId);
  if (!engagement) {
    throw new Error(`Engagement not found: ${engagementId}`);
  }

  const [accounts, subGroupings] = await Promise.all([
    db.accounts.where('engagementId').equals(engagementId).toArray(),
    db.subGroupings.where('engagementId').equals(engagementId).toArray(),
  ]);

  return {
    version: 1,
    engagement,
    accounts,
    subGroupings,
  };
}
